import { keyframes } from '@emotion/react';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { GradientSpinner, Loading } from '@/core/components';
import { useAuth } from '@/core/hooks/useAuth';
import { decryptWallet, getUserWalletData } from '@/core/services/wallet';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export function WalletPage() {
  const { user, loading: authLoading } = useAuth();
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [privateKey, setPrivateKey] = useState<string | null>(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [decrypting, setDecrypting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const fetchWallet = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const walletData = await getUserWalletData(user.uid);
      if (walletData) {
        setWalletAddress(walletData.walletAddress);

        // 秘密鍵を復号化
        setDecrypting(true);
        const { privateKey: pk } = await decryptWallet(walletData.salt, walletData.encryptedWallet);
        setPrivateKey(pk);
      } else {
        setError('ウォレット情報が見つかりません');
      }
    } catch (err) {
      console.error('Failed to fetch wallet:', err);
      setError('ウォレット情報の取得に失敗しました');
    } finally {
      setLoading(false);
      setDecrypting(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchWallet();
    }
  }, [authLoading, user, fetchWallet]);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage(`${label}をコピーしました`);
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('コピーに失敗しました');
      setSnackbarOpen(true);
    }
  };

  if (authLoading) {
    return <Loading />;
  }

  if (!user) {
    return (
      <Alert severity="warning" sx={{ animation: `${fadeInUp} 0.6s ease-out` }}>
        ログインが必要です
      </Alert>
    );
  }

  return (
    <Box>
      {/* タイトルエリア */}
      <Box
        sx={{
          textAlign: 'center',
          mt: { xs: 2, sm: 5 },
          mb: { xs: 3, sm: 5 },
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            letterSpacing: '0.2em',
            color: 'text.secondary',
            mb: 1,
            fontSize: '0.7rem',
          }}
        >
          YOUR WALLET
        </Typography>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 800,
            background: gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          ウォレット情報
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Loading />
      ) : (
        <Paper
          sx={{
            p: 2,
            position: 'relative',
            background: theme.palette.background.paper,
            border: 'none',
            borderRadius: 2,
            animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              padding: '2px',
              background: gradient,
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              pointerEvents: 'none',
            },
          }}
        >
          {/* ウォレットアドレス */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccountBalanceWalletIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold">
                ウォレットアドレス
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                value={walletAddress || ''}
                slotProps={{ input: { readOnly: true } }}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.85rem',
                    fontFamily: 'monospace',
                  },
                }}
              />
              <IconButton
                onClick={() => walletAddress && copyToClipboard(walletAddress, 'アドレス')}
                disabled={!walletAddress}
                sx={{
                  color: 'primary.main',
                  '&:hover': { background: `${theme.palette.primary.main}20` },
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 秘密鍵 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <KeyIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold">
                秘密鍵
              </Typography>
            </Box>

            {decrypting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <GradientSpinner size={20} />
                <Typography variant="body2" color="text.secondary">
                  復号化中...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  type={showPrivateKey ? 'text' : 'password'}
                  value={privateKey || ''}
                  slotProps={{ input: { readOnly: true } }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.85rem',
                      fontFamily: 'monospace',
                    },
                  }}
                />
                <IconButton
                  onClick={() => setShowPrivateKey(!showPrivateKey)}
                  disabled={!privateKey}
                  sx={{
                    color: 'primary.main',
                    '&:hover': { background: `${theme.palette.primary.main}20` },
                  }}
                >
                  {showPrivateKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
                <IconButton
                  onClick={() => privateKey && copyToClipboard(privateKey, '秘密鍵')}
                  disabled={!privateKey}
                  sx={{
                    color: 'primary.main',
                    '&:hover': { background: `${theme.palette.primary.main}20` },
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              秘密鍵は他人に教えないでください。
            </Typography>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
}
